import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Star as StarIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { TextField } from '@mui/material';
import affiliateService from '../services/affiliateService';

function Affiliates() {
  const navigate = useNavigate();
  const [affiliates, setAffiliates] = useState([]);
  const [filteredAffiliates, setFilteredAffiliates] = useState([]);
  const [stats, setStats] = useState({ totalAffiliates: 0, activeAffiliates: 0, pendingRequests: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [affiliatesData, statsData] = await Promise.all([
        affiliateService.getAffiliates(),
        affiliateService.getStats(),
      ]);
      setAffiliates(affiliatesData.data || []);
      setFilteredAffiliates(affiliatesData.data || []);
      setStats(statsData);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAffiliates(affiliates);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = affiliates.filter(affiliate => 
        affiliate.user?.first_name?.toLowerCase().includes(term) ||
        affiliate.user?.last_name?.toLowerCase().includes(term) ||
        affiliate.user?.email?.toLowerCase().includes(term) ||
        affiliate.affiliateCode?.toLowerCase().includes(term) ||
        affiliate.companyName?.toLowerCase().includes(term)
      );
      setFilteredAffiliates(filtered);
    }
  }, [searchTerm, affiliates]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleToggleActive = async (id) => {
    try {
      await affiliateService.toggleActive(id);
      showSnackbar('Statut mis à jour');
      loadData();
    } catch (error) {
      showSnackbar('Erreur lors de la mise à jour', 'error');
    }
  };

  const handleViewDetails = async (affiliate) => {
    try {
      const data = await affiliateService.getAffiliate(affiliate.id);
      setSelectedAffiliate(data);
      setOpenDialog(true);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des détails', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleViewUser = (userId) => {
    navigate(`/users?userId=${userId}&openModal=true`);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Affiliés</Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/affiliate-requests')}
        >
          {stats.pendingRequests} demande(s) en attente
        </Button>
      </Box>

      {/* Barre de recherche */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher par nom, email, code affilié, entreprise..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: searchTerm && (
              <IconButton size="small" onClick={handleClearSearch}>
                <ClearIcon />
              </IconButton>
            ),
          }}
        />
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'primary.dark' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total affiliés
              </Typography>
              <Typography variant="h3">{stats.totalAffiliates}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'success.dark' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Affiliés actifs
              </Typography>
              <Typography variant="h3">{stats.activeAffiliates}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'warning.dark' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Demandes en attente
              </Typography>
              <Typography variant="h3">{stats.pendingRequests}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Affilié</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Entreprise</TableCell>
              <TableCell align="center">Avis</TableCell>
              <TableCell align="center">Note moy.</TableCell>
              <TableCell align="center">Produits</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAffiliates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    {searchTerm ? 'Aucun résultat trouvé' : 'Aucun affilié'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAffiliates.map((affiliate) => (
              <TableRow key={affiliate.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box>
                      <Typography variant="body2">
                        {affiliate.user?.first_name} {affiliate.user?.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {affiliate.user?.email}
                      </Typography>
                    </Box>
                    <IconButton 
                      size="small" 
                      color="info"
                      onClick={() => handleViewUser(affiliate.userId)}
                      title="Voir le profil utilisateur"
                    >
                      <PersonIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={affiliate.affiliateCode} size="small" color="primary" />
                </TableCell>
                <TableCell>{affiliate.companyName || '-'}</TableCell>
                <TableCell align="center">{affiliate.totalReviews}</TableCell>
                <TableCell align="center">
                  {affiliate.averageRating && typeof affiliate.averageRating === 'number' ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <StarIcon sx={{ fontSize: 16, color: 'warning.main', mr: 0.5 }} />
                      {affiliate.averageRating.toFixed(1)}
                    </Box>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell align="center">{affiliate.totalProductRequests}</TableCell>
                <TableCell>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={affiliate.isActive}
                        onChange={() => handleToggleActive(affiliate.id)}
                        size="small"
                      />
                    }
                    label={affiliate.isActive ? 'Actif' : 'Inactif'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleViewDetails(affiliate)}>
                    <ViewIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog détails */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Détails de l'affilié</DialogTitle>
        <DialogContent>
          {selectedAffiliate && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Code affilié</Typography>
                <Typography variant="h6">{selectedAffiliate.affiliateCode}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Utilisateur</Typography>
                <Typography>
                  {selectedAffiliate.user?.first_name} {selectedAffiliate.user?.last_name}
                </Typography>
              </Grid>

              {selectedAffiliate.companyName && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Entreprise</Typography>
                  <Typography>{selectedAffiliate.companyName}</Typography>
                </Grid>
              )}

              {selectedAffiliate.website && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Site web</Typography>
                  <Typography>{selectedAffiliate.website}</Typography>
                </Grid>
              )}

              {selectedAffiliate.socialMedia && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Réseaux sociaux</Typography>
                  <Typography>{selectedAffiliate.socialMedia}</Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2 }}>Statistiques</Typography>
              </Grid>

              <Grid item xs={4}>
                <Card sx={{ bgcolor: 'background.default' }}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">Avis</Typography>
                    <Typography variant="h5">{selectedAffiliate.totalReviews}</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={4}>
                <Card sx={{ bgcolor: 'background.default' }}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">Note</Typography>
                    <Typography variant="h5">
                      {selectedAffiliate.averageRating && typeof selectedAffiliate.averageRating === 'number' 
                        ? selectedAffiliate.averageRating.toFixed(1) 
                        : '-'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={4}>
                <Card sx={{ bgcolor: 'background.default' }}>
                  <CardContent>
                    <Typography color="text.secondary" variant="caption">Produits</Typography>
                    <Typography variant="h5">{selectedAffiliate.totalProductRequests}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Affiliates;

